import { Control211, ControlTypeAhead } from "aic";
import { Button, Col, Row } from "react-bootstrap";
import { DashCircle } from "react-bootstrap-icons";
import { CompanionVariableType } from "./VariableDto";

/**
 * Props for the CompanionRow component.
 */
export interface CompanionRowProps {
  type: CompanionVariableType;
  index: number;
  variableOptions: { value: string }[];
  getVariableValue: (type: CompanionVariableType) => string;
  getAvailableTypes: (currentType?: CompanionVariableType) => CompanionVariableType[];
  handleVariableChange: (type: CompanionVariableType, value: string) => void;
  handleTypeChange: (oldType: CompanionVariableType, newType: CompanionVariableType) => void;
  handleRemove: (type: CompanionVariableType) => void;
  disabled: boolean;
}

/**
 * Individual companion variable row.
 *
 * @param props - Properties for configuring the companion variable row
 * @param props.type - Current type of this companion variable (Order, Label, or Unit)
 * @param props.index - Zero-based position of this companion in the companions array
 * @param props.variableOptions - Clinical variables from the primary dataset available for selection
 * @param props.getVariableValue - Retrieves the selected variable name for the specified companion type
 * @param props.getAvailableTypes - Returns companion types not yet used by other companions
 * @param props.handleVariableChange - Updates the variable selection for the specified companion type
 * @param props.handleTypeChange - Changes the companion type while preserving the variable selection
 * @param props.handleRemove - Removes the companion with the specified type from the list
 * @param props.disabled - Prevents user interaction with all controls when true
 */
export const CompanionRow = ({
  type,
  index,
  variableOptions,
  getVariableValue,
  getAvailableTypes,
  handleVariableChange,
  handleTypeChange,
  handleRemove,
  disabled,
}: CompanionRowProps): React.ReactElement => {
  return (
    <Row className="mb-2">
      <Col sm={6}>
        <ControlTypeAhead
          id={`companion-variable-${index}`}
          label=""
          labelWidth={0}
          value={getVariableValue(type)}
          setValue={(value) => handleVariableChange(type, value)}
          maxWidthCols={12}
          availableList={variableOptions}
          disabled={disabled}
        />
      </Col>
      <Col sm={5}>
        <Control211
          id={`companion-type-${index}`}
          label={`Companion ${index + 1} type`}
          labelWidth={0}
          maxWidthCols={12}
          value={type}
          options={getAvailableTypes(type)}
          setValue={(value) => handleTypeChange(type, value as CompanionVariableType)}
          disabled={disabled}
        />
      </Col>
      <Col
        sm={1}
        className="d-flex justify-content-center align-items-start"
      >
        <Button
          variant="link"
          className="p-0 text-danger"
          style={{ paddingTop: "0.15rem" }}
          onClick={() => handleRemove(type)}
          disabled={disabled}
          aria-label={`Remove companion ${index + 1}`}
        >
          <DashCircle size={20} />
        </Button>
      </Col>
    </Row>
  );
};

CompanionRow.displayName = "CompanionRow";
